import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import capitalize from "lodash/capitalize";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import SyncIcon from "@material-ui/icons/Sync";
import FingerprintIcon from "@material-ui/icons/Fingerprint";

import ConfirmDialog from "src/components/ConfirmDialog";
import { StatefulLoadableIconButton } from "src/components/LoadableIconButton";
import {
  setIntegrationSync,
  dispatchIntegrationImport,
} from "src/store/support/actions";
import { supportGetToken, supportIsAdmin } from "src/store/support/selector";
import { supportGetAccessToken } from "src/apiService/modules/support";
import {
  checkExtensionVersion,
  getExtensionId,
  setCookies,
  type CookieSetDetails,
} from "src/utils/extension";
import {
  areCredentialsExpired,
  buildImpersonationCookies,
  isClientIntegrationType,
  type ClientIntegrationType,
} from "src/utils/extension/impersonateCookies";

import type {
  SupportStateUserDataIntegration,
  SupportStateUserDataIntegrationEBay,
} from "src/interfaces/supportState.interface";

import IntegrationImportDialog from "./IntegrationImportDialog";
import SyncDetailsDialog from "./SyncDetailsDialog";

export interface IntegrationsProps {
  integrations: SupportStateUserDataIntegration[];
}

const DoubleClickTooltip = "Double click to edit sync details";
const ImpersonateTooltip = "Impersonate session cookies";
const RequiredImpersonateExtensionVersion = "1.0.7";

const SupportedIntegrationTypes = new Set([
  "ebay",
  "poshmark",
  "mercari",
  "etsy",
  "amazon",
  "what-not",
  "vinted",
  "depop",
]);

function platformLabel(type: SupportStateUserDataIntegration["type"]): string {
  if (type === "what-not") return "Whatnot";
  if (type === "ebay") return "eBay";
  return capitalize(type);
}

function integrationUsername(
  integration: SupportStateUserDataIntegration
): string {
  if (integration.type === "amazon") {
    return (
      integration.username || integration.sellingPartnerId || integration.id
    );
  }
  if ("username" in integration && integration.username) {
    return integration.username;
  }
  return integration.id;
}

function lastSyncDates(integration: SupportStateUserDataIntegration): {
  inventory: string | null;
  sales: string | null;
} {
  switch (integration.type) {
    case "ebay":
      return {
        inventory: integration.ebayLastSyncDateInventory || null,
        sales: integration.ebayLastSyncDateTransactions || null,
      };
    case "poshmark":
      return {
        inventory: integration.poshmarkLastSyncDateInventory || null,
        sales: integration.poshmarkLastSyncDateTransactions || null,
      };
    case "mercari":
      return {
        inventory: integration.mercariLastSyncDateInventory || null,
        sales: integration.mercariLastSyncDateTransactions || null,
      };
    case "etsy":
      return {
        inventory: integration.etsyLastSyncDateInventory || null,
        sales: integration.etsyLastSyncDateSales || null,
      };
    case "amazon":
      return {
        inventory: integration.lastSyncDateInventory || null,
        sales: integration.lastSyncDateTransactions || null,
      };
    case "what-not":
      return {
        inventory: null,
        sales: integration.whatnotLastSyncDateTransactions || null,
      };
    case "vinted":
      return {
        inventory: integration.vintedLastSyncDateInventory || null,
        sales: integration.vintedLastSyncDateSales || null,
      };
    case "depop":
      return {
        inventory: integration.depopLastSyncDateInventory || null,
        sales: integration.depopLastSyncDateSales || null,
      };
    default:
      return { inventory: null, sales: null };
  }
}

function IntegrationEBayRow({
  integration,
}: {
  integration: SupportStateUserDataIntegrationEBay;
}) {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState<
    "import-open" | "import-loading" | "sync-open" | null
  >(null);
  return (
    <>
      <IntegrationImportDialog
        open={dialog === "import-open" || dialog === "import-loading"}
        loading={dialog === "import-loading"}
        onClose={() => setDialog(null)}
        onImport={async (dataType, dates) => {
          setDialog("import-loading");
          if (dataType === "Inventory")
            await dispatch(
              dispatchIntegrationImport(
                integration.id,
                "direct-import-inventory"
              )
            );
          else if (dates)
            await dispatch(
              dispatchIntegrationImport(
                integration.id,
                "direct-import-sales",
                dates
              )
            );
          setDialog(null);
        }}
      />
      <SyncDetailsDialog
        open={dialog === "sync-open"}
        integrationId={integration.id}
        onClose={() => setDialog(null)}
        lastSyncDateInventory={integration.ebayLastSyncDateInventory}
        lastSyncDateSales={integration.ebayLastSyncDateTransactions}
      />
      <Tooltip title={DoubleClickTooltip}>
        <TableRow onDoubleClick={() => setDialog("sync-open")}>
          <TableCell>eBay</TableCell>
          <TableCell>{integration.username}</TableCell>
          <TableCell align="center">
            <Button color="primary" onClick={() => setDialog("import-open")}>
              Import
            </Button>
          </TableCell>
          <TableCell align="center">
            <Checkbox
              checked={integration.sync}
              onChange={(ev) => {
                const checked = ev.target.checked;
                dispatch(
                  setIntegrationSync(integration.id, {
                    sync: checked,
                  })
                );
              }}
            />
          </TableCell>
          <TableCell align="center">
            <Checkbox
              checked={integration.sync && integration.inventory !== false}
              disabled={!integration.sync}
              onChange={(ev) => {
                const checked = ev.target.checked;
                dispatch(
                  setIntegrationSync(integration.id, {
                    inventory: checked,
                  })
                );
              }}
            />
          </TableCell>
          <TableCell align="center">
            <StatefulLoadableIconButton
              onClick={() =>
                dispatch(
                  dispatchIntegrationImport(integration.id, "daily-sync")
                )
              }
              color="primary"
            >
              <SyncIcon />
            </StatefulLoadableIconButton>
          </TableCell>
        </TableRow>
      </Tooltip>
    </>
  );
}

const ImpersonateConfirmText =
  "This will set the user's marketplace cookies in your current browser so you can access their session. Continue?";
const ImpersonateExpiredConfirmText =
  "These credentials appear to have expired. Setting them may not give you a working session. You can still set the cookies anyway if you want to try.";

type PendingImpersonation = {
  cookies: CookieSetDetails[];
  expired: boolean;
};

function ImpersonateButton({
  integrationId,
  platform,
}: {
  integrationId: string;
  platform: ClientIntegrationType;
}) {
  const token = useSelector(supportGetToken);
  const isAdmin = useSelector(supportIsAdmin);
  const [pending, setPending] = useState<PendingImpersonation | null>(null);

  if (!isAdmin) return null;

  const confirmOpen = pending != null;

  return (
    <>
      <Tooltip title={ImpersonateTooltip}>
        <span>
          <StatefulLoadableIconButton
            color="primary"
            onClick={async () => {
              checkExtensionVersion(RequiredImpersonateExtensionVersion);
              const { data } = await supportGetAccessToken(
                token,
                integrationId
              );
              const cookies = buildImpersonationCookies(platform, data);
              if (cookies.length === 0) {
                throw new Error(
                  "No cookies available to set for this integration"
                );
              }
              setPending({
                cookies,
                expired: areCredentialsExpired(platform, data),
              });
            }}
          >
            <FingerprintIcon />
          </StatefulLoadableIconButton>
        </span>
      </Tooltip>
      <ConfirmDialog
        open={confirmOpen}
        title="Impersonate integration"
        text={
          pending?.expired
            ? ImpersonateExpiredConfirmText
            : ImpersonateConfirmText
        }
        cancel="Cancel"
        confirm={pending?.expired ? "Set cookies anyway" : "Set cookies"}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          if (!pending) return;
          try {
            const extensionId = getExtensionId();
            await setCookies(extensionId, pending.cookies);
            toast.success("Cookies set correctly");
            setPending(null);
          } catch (e) {
            toast.error((e as Error).message || String(e));
          }
        }}
      />
    </>
  );
}

function IntegrationClientRow({
  integration,
}: {
  integration: Exclude<
    SupportStateUserDataIntegration,
    SupportStateUserDataIntegrationEBay
  >;
}) {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState<"sync-open" | null>(null);
  const syncDates = lastSyncDates(integration);

  return (
    <>
      <SyncDetailsDialog
        open={dialog === "sync-open"}
        integrationId={integration.id}
        onClose={() => setDialog(null)}
        lastSyncDateInventory={syncDates.inventory}
        lastSyncDateSales={syncDates.sales}
        enableInventory={integration.type !== "what-not"}
      />
      <Tooltip title={DoubleClickTooltip}>
        <TableRow onDoubleClick={() => setDialog("sync-open")}>
          <TableCell>{platformLabel(integration.type)}</TableCell>
          <TableCell>{integrationUsername(integration)}</TableCell>
          <TableCell align="center">
            <Tooltip title="Can't dispatch an import for a client integration">
              <span>
                <Button color="primary" disabled>
                  Import
                </Button>
              </span>
            </Tooltip>
          </TableCell>
          <TableCell align="center">
            <Checkbox
              checked={integration.sync}
              onChange={(ev) => {
                const checked = ev.target.checked;
                dispatch(
                  setIntegrationSync(integration.id, {
                    sync: checked,
                  })
                );
              }}
            />
          </TableCell>
          <TableCell align="center">
            <Checkbox
              checked={integration.sync && integration.inventory !== false}
              disabled={!integration.sync}
              onChange={(ev) => {
                const checked = ev.target.checked;
                dispatch(
                  setIntegrationSync(integration.id, {
                    inventory: checked,
                  })
                );
              }}
            />
          </TableCell>
          <TableCell align="center">
            <Box display="flex" justifyContent="center" alignItems="center">
              <Tooltip title="Can't dispatch an import for a client integration">
                <span>
                  <IconButton disabled color="primary">
                    <SyncIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {isClientIntegrationType(integration.type) ? (
                <ImpersonateButton
                  integrationId={integration.id}
                  platform={integration.type}
                />
              ) : null}
            </Box>
          </TableCell>
        </TableRow>
      </Tooltip>
    </>
  );
}

function Integrations({ integrations }: IntegrationsProps) {
  const validIntegrations = integrations.filter((i) =>
    SupportedIntegrationTypes.has(i.type)
  );

  return (
    <Box mx={2} my={1}>
      <Typography variant="h6">Platform Integrations</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Platform</TableCell>
            <TableCell>Username</TableCell>
            <TableCell align="center">Import Past Data</TableCell>
            <TableCell align="center">
              Daily Integration - Sales and Fees
            </TableCell>
            <TableCell align="center">Daily Integration - Inventory</TableCell>
            <TableCell align="center">Pull Data Now</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {validIntegrations.length > 0 ? (
            validIntegrations.map((integration) =>
              integration.type === "ebay" ? (
                <IntegrationEBayRow
                  key={integration.id}
                  integration={integration}
                />
              ) : (
                <IntegrationClientRow
                  key={integration.id}
                  integration={integration}
                />
              )
            )
          ) : (
            <TableRow>
              <TableCell align="center" colSpan={6}>
                Empty
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

export default Integrations;
